import { Connection, PublicKey, Transaction } from "@solana/web3.js";
import BigNumber from "bignumber.js";
import { TOKEN_PROGRAM_ID, getAssociatedTokenAddressSync, getTokenMetadata } from "@solana/spl-token";
import { createUmi } from "@metaplex-foundation/umi-bundle-defaults";
import { isNonFungible } from "@metaplex-foundation/mpl-token-metadata";
import { unwrapOption } from "@metaplex-foundation/umi";
import {
  Asset,
  BidAsset,
  BidInfo,
  BidderInfo,
  SaleInfo,
  SoldOutBidderIncentivesInfo,
  SpecificBidsIncentivesInfo,
  createBidInstruction,
  errorFromCode,
} from "@axvdex/solana/outbidAPI/js/generated";
import { localStaticOutbids } from "@axvdex/mocks/DummySolanaOutbitData";
import { Bidder, SaleBidAsset, OutbidSale, Sale, SpecificBidAsset, DigitalAsset } from "@axvdex/types/solana/outbid";
import {
  findBid,
  findBidder,
  findSoldOutBidderIncentives,
  findSpecificBidsIncentives,
  findTransferAuthority,
} from "./utils/DeriveOutbidPDA";
import { processError, sendWithPhantomAndDebugTransaction, verifySolanaClientOrThrow } from "./utils/client";
import { fetchDigitalAsset } from "./utils/asset";

// CONST ======================================================================
const SOLANA_OUTBIDS_PROGRAM_ID = process.env.REACT_APP_SOLANA_OUTBID_PROGRAM_ID;
export const SOLANA_OUTBIDS_PROGRAM_PUBLICK_KEY = new PublicKey(SOLANA_OUTBIDS_PROGRAM_ID);
export const SOLANA_RPC_URL = process.env.REACT_APP_SOLANA_NETWORK;

// Get the outbids ============================================================

export async function useGetOutbidSales(
  filter: "upcoming" | "live" | "vesting" | "completed" | "all"
): Promise<OutbidSale[]> {
  // Test Function to query the PDA of the outbids program
  let outbidSales: OutbidSale[] = [];
  if (process.env.NODE_ENV !== "production" || process.env.REACT_APP_MODE === "TESTNET") {
    outbidSales = localStaticOutbids;
  } else {
    outbidSales = await getOutbids();
  }
  if (filter === "all") {
    return outbidSales;
  }
  return outbidSales.filter(outbid => outbid.status === filter);
}

async function getOutbids(): Promise<OutbidSale[]> {
  // TODO:
  throw new Error("Not Implemented");
}

// Get the sale info =================================================

export async function useGetSales(connection: Connection, saleAccounts: PublicKey[]): Promise<Sale[]> {
  const sales = await Promise.all(
    saleAccounts.map(async saleAccount => {
      return await getSale(connection, saleAccount);
    })
  );
  return sales;
}

// Get the Sale info.
// @param connection - The connection to the solana network
// @param saleAccount - The sale account address
// @returns The sale info
// @throws Error - If a deserilization of an on-chain account failed
export async function getSale(connection: Connection, saleAccount: PublicKey): Promise<Sale> {
  // Fetch sale account info
  const saleAccountInfo = await connection.getAccountInfo(saleAccount);
  if (!saleAccountInfo) {
    throw new Error("Error - Sale account not found");
  }
  const saleInfo = SaleInfo.fromAccountInfo(saleAccountInfo)[0];

  // Mapping SaleInfo to Sale
  const sale: Sale = {
    id: Number(saleInfo.config.id.toString()), // Convert to string and then to number
    saleAccount: saleAccount,
    authority: saleInfo.authority,
    status: getStatusFromSaleInfo(saleInfo),
    config: {
      bidAssets: await Promise.all(
        saleInfo.config.bidAssets.map(async bidAsset => {
          return await retreiveBidAssetState(connection, bidAsset);
        })
      ),
      isCancelled: saleInfo.config.isCancelled,
      isStopped: saleInfo.config.isStopped,
      isProcessEnded: saleInfo.config.isProcessEnded,
      saleSettings: {
        bonusDuration: BigNumber(saleInfo.config.saleSettings.bonusDuration.toString()).toNumber(),
        bonusMinTicketIncrease: BigNumber(saleInfo.config.saleSettings.bonusMinTicketIncrease.toString()),
        bonusSellOutBonusTokenPer: BigNumber(saleInfo.config.saleSettings.bonusSellOutBonusTokenPer.toString()),
        bonusSellOutElegibleTokenPer: BigNumber(saleInfo.config.saleSettings.bonusSellOutEligibleTokenPer.toString()),
        duration: BigNumber(saleInfo.config.saleSettings.duration.toString()).toNumber(),
        vestingDuration: BigNumber(saleInfo.config.saleSettings.vestingDuration.toString()).toNumber(),
        minTicket: BigNumber(saleInfo.config.saleSettings.minTicket.toString()),
        maxTicket: BigNumber(saleInfo.config.saleSettings.maxTicket.toString()),
        walletMaxTicketCap:
          saleInfo.config.saleSettings.walletMaxTicketCap !== null
            ? BigNumber(saleInfo.config.saleSettings.walletMaxTicketCap.toString())
            : null,
        whitelistDuration: BigNumber(saleInfo.config.saleSettings.whitelistDuration.toString()).toNumber(),
      },
      targetAllocation: {
        targetTicketAmount: BigNumber(saleInfo.config.targetAllocation.targetTicketAmount.toString()),
        targetAssetAllocationAmount: BigNumber(saleInfo.config.targetAllocation.targetAssetAllocationAmount.toString()),
        targetAssetDecimals: BigNumber(saleInfo.config.targetAllocation.targetAssetDecimals.toString()),
        targetAssetMint: saleInfo.config.targetAllocation.targetAssetMint,
        ratioTicketToTargetAsset: BigNumber(
          saleInfo.config.targetAllocation.targetAssetAllocationAmount.toString()
        ).div(BigNumber(saleInfo.config.targetAllocation.targetTicketAmount.toString())),
      },
    },
    lastestBidder: saleInfo.latestBidder,
    saleState: {
      bidsCount: BigNumber(saleInfo.saleState.bidsCount.toString()),
      bonusTimeTriggerCount: BigNumber(saleInfo.saleState.bonusTimeTriggerCount.toString()),
      cancelTimestamp: saleInfo.saleState.cancelTimestamp
        ? BigNumber(saleInfo.saleState.cancelTimestamp.toString()).toNumber()
        : null,
      endTimestamp: BigNumber(saleInfo.saleState.endTimestamp.toString()).toNumber(),
      soldOutTimestamp: saleInfo.saleState.soldOutTimestamp
        ? BigNumber(saleInfo.saleState.soldOutTimestamp.toString()).toNumber()
        : null,
      endVestingTimestamp: getEndVestingTimestamp(saleInfo),
      startTimestamp: BigNumber(saleInfo.saleState.startTimestamp.toString()).toNumber(),
      ticketsCount: BigNumber(saleInfo.saleState.ticketsCount.toString()),
    },
    topBidders: saleInfo.topBidders
      .filter(bidder => !bidder.key.equals(PublicKey.default))
      .map(bidder => ({
        address: bidder.key,
        totalTicketAmount: Number(bidder.boughtTicketsAmount.toString()),
      })),
    specificBidIncentives: await getSpecificIncentives(connection, saleAccount),
    soldOutBidderIncentives: await getSoldOutIncentives(connection, saleAccount),
    // TODO: When backend is ready
    activityMessages: [],
  };
  console.log("Sale", sale);
  return sale;
}

export async function retreiveBidAssetState(connection: Connection, bidAsset: BidAsset): Promise<SaleBidAsset> {
  const fullBidAsset: SaleBidAsset = {
    mint: bidAsset.mint,
    decimals: bidAsset.decimals,
    assetToTicketRatio: new BigNumber(bidAsset.assetToTicketRatio.toString()),
    symbol: null,
    price: null,
  };
  try {
    const metadata = await getTokenMetadata(connection, bidAsset.mint);
    console.log("metadata", metadata);
  } catch (error) {
    console.warn("No metadata found for mint", bidAsset.mint.toBase58());
    fullBidAsset.symbol = fullBidAsset.mint.toBase58().slice(0, 4);
  }
  return fullBidAsset;
}

// Helper function to determine the status
function getStatusFromSaleInfo(saleInfo: SaleInfo): Sale["status"] {
  const now = Date.now() / 1000;
  const startTimestamp = Number(saleInfo.saleState.startTimestamp.toString());
  const endTimestamp = Number(saleInfo.saleState.endTimestamp.toString());
  const whitelistEndTimestamp = startTimestamp + Number(saleInfo.config.saleSettings.whitelistDuration.toString());
  const bonusStartTimestamp = endTimestamp - Number(saleInfo.config.saleSettings.bonusDuration.toString());
  const soldOutTimestamp = Number(saleInfo.saleState.soldOutTimestamp?.toString());

  if (now < startTimestamp) {
    return "WaitingToStart";
  } else if (soldOutTimestamp && now >= soldOutTimestamp) {
    return "EndedSoldOut";
  } else if (now >= startTimestamp && now < whitelistEndTimestamp) {
    return "WhitelistBidding";
  } else if (now >= whitelistEndTimestamp && now < bonusStartTimestamp) {
    return "Bidding";
  } else if (now >= bonusStartTimestamp && now < endTimestamp) {
    return "BonusBidding";
  } else if (
    now >= endTimestamp &&
    new BigNumber(saleInfo.saleState.ticketsCount.toString()).isEqualTo(
      new BigNumber(saleInfo.config.targetAllocation.targetTicketAmount.toString())
    )
  ) {
    return "EndedSoldOut";
  } else if (
    now >= endTimestamp &&
    new BigNumber(saleInfo.saleState.ticketsCount.toString()).isLessThan(
      new BigNumber(saleInfo.config.targetAllocation.targetTicketAmount.toString())
    )
  ) {
    return "EndedNotSoldOut";
  }
  return "EndedBonusTimeSoldOut";
}

function getEndVestingTimestamp(sale: SaleInfo): number {
  let startWait = new BigNumber(sale.saleState.endTimestamp.toString());
  if (sale.saleState.soldOutTimestamp) {
    startWait = new BigNumber(sale.saleState.soldOutTimestamp.toString());
  }
  const endWait = startWait.plus(new BigNumber(sale.config.saleSettings.vestingDuration.toString()));
  return endWait.toNumber();
}

// GET INCENTIVES ============================================================

export async function getSpecificIncentives(
  connection: Connection,
  saleAccount: PublicKey
): Promise<Map<string, DigitalAsset[]>> {
  // Find PDA
  const [incentivesAccount] = findSpecificBidsIncentives(saleAccount);

  // Fetch account info
  const incentivesAccountInfo = await connection.getAccountInfo(incentivesAccount);
  if (!incentivesAccountInfo) {
    console.warn("No specific incentive account found.");
    return null; // No account found
  }

  // Convert buffer to a type object
  const incentives = SpecificBidsIncentivesInfo.fromAccountInfo(incentivesAccountInfo)[0];

  // To fetch the metadata
  const umi = createUmi(connection.rpcEndpoint);

  // Convert all incentvie to DigitalAsset
  const incentivesMap = new Map<string, DigitalAsset[]>();

  for (const [key, value] of incentives.specificBidsIncentives) {
    const digitalAssets: DigitalAsset[] = await Promise.all(
      value.map(async asset => {
        return fetchDigitalAssetInfo(umi, asset.mint, BigNumber(asset.amount.toString()));
      })
    );
    incentivesMap.set(key.toString(), digitalAssets);
  }

  return incentivesMap;
}

export async function getSoldOutIncentives(connection: Connection, saleAccount: PublicKey): Promise<DigitalAsset[]> {
  // Find PDA
  const [incentivesAccount] = findSoldOutBidderIncentives(saleAccount);

  // Fetch account info
  const incentivesAccountInfo = await connection.getAccountInfo(incentivesAccount);
  if (!incentivesAccountInfo) {
    console.warn("No sold out incentive account found.");
    return null; // No account found
  }

  // Convert buffer to a type object
  const incentives = SoldOutBidderIncentivesInfo.fromAccountInfo(incentivesAccountInfo)[0];

  // To fetch the metadata
  const umi = createUmi(connection.rpcEndpoint);

  // Convert all BN | Number to BigNumber & fetch the metadata to know if it's an NFT
  const digitalAssets: DigitalAsset[] = await Promise.all(
    incentives.soldOutBidderIncentives.map(async asset => {
      return fetchDigitalAssetInfo(umi, asset.mint, BigNumber(asset.amount.toString()));
    })
  );

  return digitalAssets;
}

async function fetchDigitalAssetInfo(umi: any, mint: PublicKey, amount: BigNumber): Promise<DigitalAsset> {
  try {
    const digitalAsset = await fetchDigitalAsset(umi, mint);
    if (isNonFungible(unwrapOption(digitalAsset.metadata.tokenStandard))) {
      return {
        mint: mint,
        amount: amount,
        isNFT: true,
        isAsset: false,
      };
    }
  } catch (error) {
    console.warn("Error fetching digital asset", error);
  }
  return { mint: mint, amount: amount, isNFT: false, isAsset: true };
}

// GET BIDDER INFO ============================================================

export async function useGetBidderInfo(
  connection: Connection,
  saleAccount: PublicKey,
  bidderAuthorityAccount: PublicKey
): Promise<Bidder | null> {
  // Find PDA for the bidder
  const [bidderAccount] = findBidder(saleAccount, bidderAuthorityAccount);

  // Fetch sale account info
  const bidderAccountInfo = await connection.getAccountInfo(bidderAccount);
  if (!bidderAccountInfo) {
    console.warn("No bidder account found.");
    return null; // No account found
  }

  // Fetch the bidder info e.g. metadata
  const bidderInfo = BidderInfo.fromAccountInfo(bidderAccountInfo)[0];

  // Fetch the bids related to this user -  INFO: the bids array is composed of the bids' id
  const bidsInfo = await Promise.all(
    bidderInfo.bids.map(async bidId => getBidInfo(connection, saleAccount, Number(bidId.toString())))
  );

  // Map return to the right type (preprocessing if needed for the type to match)
  const bidder: Bidder = {
    boughtTicketsAmount: BigNumber(bidderInfo.boughtTicketsAmount.toString()),
    eligibleBonusAmount: BigNumber(bidderInfo.eligibleBonusAmount.toString()),
    bidIds: bidderInfo.bids.map(bidId => BigNumber(bidId.toString())),
    bids: bidsInfo.map(bidInfo => {
      return {
        id: BigNumber(bidInfo.id.toString()),
        timestamp: BigNumber(bidInfo.timestamp.toString()),
        tickerAmount: BigNumber(bidInfo.ticketAmount.toString()),
        bonusEligibleAmount: bidInfo.refundedTimestamp ? BigNumber(bidInfo.bonusEligibleAmount.toString()) : null,
        bidAsset: {
          mint: bidInfo.bidAsset.mint,
          amount: BigNumber(bidInfo.bidAsset.amount.toString()),
        },
        bidder: bidInfo.bidder,
        refunded: bidInfo.refunded,
        refundedTimestamp: bidInfo.refundedTimestamp ? BigNumber(bidInfo.refundedTimestamp.toString()) : null,
        redeemed: bidInfo.redeemed,
        redeemedTimestamp: bidInfo.redeemedTimestamp ? BigNumber(bidInfo.redeemedTimestamp.toString()) : null,
      };
    }),
  };

  return bidder;
}

// GET BID INFO ===============================================================

export async function getBidInfo(connection: Connection, saleAccount: PublicKey, bidId: number): Promise<BidInfo> {
  const [bidAccount] = findBid(saleAccount, bidId);
  const bidAccountInfo = await connection.getAccountInfo(bidAccount);
  if (!bidAccountInfo) {
    throw new Error("Error - bid account not found");
  }
  // TBD: Mapping to a custom type ?
  const bidInfo = BidInfo.fromAccountInfo(bidAccountInfo)[0];
  return bidInfo;
}

export async function useGetBids(connection: Connection, sale: Sale): Promise<BidInfo[]> {
  const saleAccount = sale.saleAccount;
  const bidsCount = sale.saleState.bidsCount.toNumber();
  const bidsInfo = await Promise.all(
    Array.from({ length: bidsCount }, (_, i) => getBidInfo(connection, saleAccount, i))
  );
  return bidsInfo;
}

// GET ASSETS BALANCE =========================================================

export type Mint = string;
export type AssetBalance = { [key: Mint]: number };
export async function fetchGetAssetsBalance(
  connection: Connection,
  publicKey: PublicKey | string
): Promise<AssetBalance> {
  if (typeof publicKey === "string") {
    publicKey = new PublicKey(publicKey);
  }
  verifySolanaClientOrThrow();

  // Retreive the token addresses for the connected wallet
  const assetsBalance: AssetBalance = {};
  const tokenAccounts = await connection.getParsedTokenAccountsByOwner(publicKey, {
    programId: TOKEN_PROGRAM_ID,
  });
  for (const account of tokenAccounts.value) {
    assetsBalance[account.account.data.parsed.info.mint] = Number(
      account.account.data.parsed.info.tokenAmount.uiAmount
    );
  }
  return assetsBalance;
}

// PLACE BID ==================================================================

export type BidArgs = {
  bidAssetIndex: number;
  bidAssetAmount: number;
};
export async function placeBid(
  connection: Connection,
  sale: Sale,
  bidArgs: BidArgs,
  ticketamount: BigNumber
): Promise<SpecificBidAsset[]> {
  // Pre flight checks
  verifySolanaClientOrThrow();
  const incentivesWon: SpecificBidAsset[] = [];

  // Find the transfer authority
  const saleAccount = sale.saleAccount;
  const [transferAuthority] = findTransferAuthority(saleAccount);

  // Retreive the src and dest accounts to place the bid with
  const walletPublicKey = window.solanaClient.publicKey;
  const tokenAccounts = await connection.getParsedTokenAccountsByOwner(walletPublicKey, {
    programId: TOKEN_PROGRAM_ID,
  });
  const mint = sale.config.bidAssets[bidArgs.bidAssetIndex].mint;
  const srcTokenAccount = tokenAccounts.value.find(
    account => account.account.data.parsed.info.mint === mint.toBase58()
  );
  const destTokenAccount = getAssociatedTokenAddressSync(mint, transferAuthority, true);

  // Build the bid instruction
  const [soldOutBidderIncentives] = findSoldOutBidderIncentives(saleAccount);
  const [specificBidsIncentives] = findSpecificBidsIncentives(saleAccount);
  const [bidderPDA] = findBidder(saleAccount, window.solanaClient.publicKey);
  const [bidPDA] = findBid(saleAccount, sale.saleState.bidsCount.toNumber());
  const bidIx = createBidInstruction(
    {
      saleAccount: saleAccount,
      transferAuthority: transferAuthority,
      soldOutBidderIncentives: soldOutBidderIncentives,
      specificBidsIncentives: specificBidsIncentives,
      bidder: bidderPDA,
      bidderAuthority: window.solanaClient.publicKey,
      bid: bidPDA,
      bidAssetDest: destTokenAccount,
      bidAssetSrc: srcTokenAccount.pubkey,
      bidAssetMint: mint,
    },
    {
      bidArgs: bidArgs,
    }
  );

  // `createBidInstruction` doesn't support multiple incentive, so we need to attach the key manually
  // 1. Add the keys for SoldOutIncentives
  const remainingTickets = sale.config.targetAllocation.targetTicketAmount.minus(sale.saleState.ticketsCount);
  if (ticketamount.gte(remainingTickets)) {
    const soldOutIncentives = sale.soldOutBidderIncentives;
    for (const incentive of soldOutIncentives) {
      incentivesWon.push(incentive);
      const userAccount = getAssociatedTokenAddressSync(incentive.mint, walletPublicKey, true);
      const fromTokenAccount = getAssociatedTokenAddressSync(incentive.mint, transferAuthority, true);
      bidIx.keys.push({
        pubkey: userAccount,
        isWritable: true,
        isSigner: false,
      });
      bidIx.keys.push({
        pubkey: fromTokenAccount,
        isWritable: true,
        isSigner: false,
      });
      bidIx.keys.push({
        pubkey: incentive.mint,
        isWritable: false,
        isSigner: false,
      });
    }
  }

  // 2. Add the keys for SpecificIncentives
  const specificIncentives = sale.specificBidIncentives.get(sale.saleState.bidsCount.toString());
  if (specificIncentives) {
    for (const incentive of specificIncentives) {
      console.log("Adding specific incentive", incentive);
      incentivesWon.push(incentive);
      const userAccount = getAssociatedTokenAddressSync(incentive.mint, walletPublicKey, true);
      const fromTokenAccount = getAssociatedTokenAddressSync(incentive.mint, transferAuthority, true);
      bidIx.keys.push({
        pubkey: userAccount,
        isWritable: true,
        isSigner: false,
      });
      bidIx.keys.push({
        pubkey: fromTokenAccount,
        isWritable: true,
        isSigner: false,
      });
      bidIx.keys.push({
        pubkey: incentive.mint,
        isWritable: false,
        isSigner: false,
      });
    }
  }

  const tx = new Transaction();
  tx.add(bidIx);
  tx.feePayer = window.solanaClient.publicKey;
  tx.recentBlockhash = (await connection.getLatestBlockhash()).blockhash;

  try {
    // const signature = await sendWithPhantomAndDebugTransaction(tx, connection, errorFromCode);
    const signature = await window.solanaClient.sendTransaction(tx, connection);
    await connection.confirmTransaction(signature, "finalized");
  } catch (error) {
    console.error(processError(error.message, errorFromCode));
    throw error;
  }

  return incentivesWon;
}
